import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { RiskBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_COLORS = {
  submitted: { bg: "var(--primary-soft)", color: "var(--primary)" },
  identity_verification_pending: { bg: "var(--warn-soft)", color: "var(--warn)" },
  under_review: { bg: "var(--primary-soft)", color: "var(--primary)" },
  assigned: { bg: "var(--primary-soft)", color: "var(--primary)" },
  information_required: { bg: "var(--warn-soft)", color: "var(--warn)" },
  response_drafted: { bg: "var(--warn-soft)", color: "var(--warn)" },
  approved: { bg: "var(--success-soft)", color: "var(--success)" },
  completed: { bg: "var(--success-soft)", color: "var(--success)" },
  rejected: { bg: "var(--danger-soft)", color: "var(--danger)" },
  escalated: { bg: "var(--danger-soft)", color: "var(--danger)" },
  closed: { bg: "var(--bg-2)", color: "var(--muted)" },
};

const SLA_COLORS = {
  on_track: { bg: "var(--success-soft)", color: "var(--success)" },
  at_risk: { bg: "var(--warn-soft)", color: "var(--warn)" },
  breached: { bg: "var(--danger-soft)", color: "var(--danger)" },
  closed: { bg: "var(--bg-2)", color: "var(--muted)" },
  unknown: { bg: "var(--bg-2)", color: "var(--muted)" },
};

function Pill({ label, mapObj, testId }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {(label || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

export default function RightsRequests() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "org_admin", "dpo", "legal_reviewer"].includes(user?.role);
  const canConfig = ["super_admin", "org_admin", "dpo"].includes(user?.role);

  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ request_types: [], statuses: [], departments: [], sla_config: {} });
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [fStatus, setFStatus] = useState("");
  const [fType, setFType] = useState("");
  const [fDept, setFDept] = useState("");
  const [fSla, setFSla] = useState("");
  const [fSearch, setFSearch] = useState("");

  // Modals
  const [newModal, setNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ principal_name: "", principal_email: "", request_type: "access", description: "", priority: "medium" });
  const [detail, setDetail] = useState(null);
  const [assignForm, setAssignForm] = useState({ department: "dpo", assigned_to: "", note: "" });
  const [infoForm, setInfoForm] = useState({ items: "", message: "" });
  const [draftText, setDraftText] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [escalateForm, setEscalateForm] = useState({ reason: "", escalate_to: "" });
  const [completeSummary, setCompleteSummary] = useState("");
  const [slaModal, setSlaModal] = useState(false);
  const [slaForm, setSlaForm] = useState({});

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, d, l] = await Promise.all([
        api.get("/principal-requests-meta/config"),
        api.get("/principal-requests-dashboard"),
        api.get("/principal-requests", { params: { limit: 500, status: fStatus, request_type: fType, department: fDept, search: fSearch, sla_state: fSla } }),
      ]);
      setMeta(m.data);
      setDash(d.data);
      setItems(l.data.items || []);
      setSlaForm(m.data.sla_config || {});
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { loadAll();   }, []);
  useEffect(() => { if (tab === "list") loadAll();   }, [fStatus, fType, fDept, fSla, fSearch]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/principal-requests/${id}`);
      setDetail(data);
      setAssignForm({ department: data.assigned_department || "dpo", assigned_to: data.assigned_to || "", note: "" });
      setDraftText(data.response_draft || data.ai_analysis?.draft_response || "");
      setInfoForm({ items: "", message: "" });
      setRejectReason(""); setEscalateForm({ reason: "", escalate_to: "" }); setCompleteSummary("");
    } catch { toast.error("Failed to fetch"); }
  };

  const createRequest = async () => {
    try {
      await api.post("/principal-requests", newForm);
      toast.success("Request created");
      setNewModal(false);
      setNewForm({ principal_name: "", principal_email: "", request_type: "access", description: "", priority: "medium" });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doAction = async (path, body, msg) => {
    try {
      await api.post(`/principal-requests/${detail.id}/${path}`, body || {});
      toast.success(msg);
      const { data } = await api.get(`/principal-requests/${detail.id}`);
      setDetail(data);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const aiAnalyze = async () => {
    toast.info("Running AI analysis...");
    try {
      const { data } = await api.post(`/principal-requests/${detail.id}/ai-analyze`);
      setDetail({ ...detail, ai_analysis: data });
      if (data.draft_response && !draftText) setDraftText(data.draft_response);
      toast.success("AI analysis complete");
    } catch { toast.error("AI failed"); }
  };

  const uploadResponseDoc = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/principal-requests/${detail.id}/response-document`, fd);
      toast.success(`Response document uploaded: ${data.filename}`);
      const r = await api.get(`/principal-requests/${detail.id}`);
      setDetail(r.data);
    } catch { toast.error("Upload failed"); }
  };

  const uploadInternalAttachment = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/principal-requests/${detail.id}/attach`, fd);
      toast.success("Attachment uploaded");
      const r = await api.get(`/principal-requests/${detail.id}`);
      setDetail(r.data);
    } catch { toast.error("Upload failed"); }
  };

  const saveSla = async () => {
    try {
      await api.put("/principal-requests-meta/sla", { dsar_sla: slaForm });
      toast.success("SLA saved");
      setSlaModal(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const grouped = useMemo(() => {
    // Kanban lanes
    const laneKeys = ["submitted", "under_review", "assigned", "information_required", "response_drafted", "approved", "escalated"];
    const lanes = laneKeys.map(k => ({ key: k, label: k.replace(/_/g, " "), items: [] }));
    items.forEach(it => {
      const lane = lanes.find(l => l.key === it.status);
      if (lane) lane.items.push(it);
    });
    return lanes;
  }, [items]);

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Rights Request Workbench"
        kicker="DPDP Sec. 11–Sec. 14 Data Principal Rights"
        description="Full DSAR lifecycle: intake → verify → assign → search → draft → approve → complete. AI-assisted classification, drafting and risk-flagging."
        testId="rights-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={loadAll} data-testid="rights-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            {canConfig && (
              <Btn variant="secondary" onClick={() => setSlaModal(true)} data-testid="rights-sla-btn"><Icons.Timer className="w-3.5 h-3.5" /> SLA config</Btn>
            )}
            <Btn onClick={() => setNewModal(true)} data-testid="rights-new-btn"><Icons.Plus className="w-3.5 h-3.5" /> New request</Btn>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "list", label: "All requests", icon: "List" },
          { key: "kanban", label: "Workflow board", icon: "Columns" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`rights-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total requests" value={dash.total_requests} testId="stat-total" />
            <StatCard label="Pending" value={dash.pending_requests} tone="warn" testId="stat-pending" />
            <StatCard label="Overdue" value={dash.overdue_requests} tone="danger" testId="stat-overdue" hint={`${dash.at_risk_requests || 0} at risk`} />
            <StatCard label="Completed" value={dash.completed_requests} tone="success" testId="stat-completed" />
            <StatCard label="SLA compliance" value={`${dash.sla_compliance_pct}%`}
              tone={dash.sla_compliance_pct >= 90 ? "success" : dash.sla_compliance_pct >= 70 ? "warn" : "danger"}
              testId="stat-sla" hint="Closed on time / total closed" />
            <StatCard label="Avg closure time" value={`${dash.avg_closure_hours}h`} testId="stat-closure" />
            <StatCard label="Types" value={Object.values(dash.by_type || {}).reduce((a, b) => a + b, 0)} testId="stat-types" />
            <StatCard label="Escalated" value={dash.by_status?.escalated || 0} tone="danger" testId="stat-escalated" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By status</div>
              <div className="space-y-1.5" data-testid="by-status-list">
                {Object.entries(dash.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => {
                  const total = Object.values(dash.by_status || {}).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round(v / total * 100);
                  return (
                    <div key={k}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 text-[11.5px] ink">{k.replace(/_/g, " ")}</div>
                        <div className="text-[11px] muted">{v} ({pct}%)</div>
                      </div>
                      <div className="h-1.5 rounded-full mt-0.5 overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: STATUS_COLORS[k]?.color || "var(--primary)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By request type</div>
              <div className="space-y-1.5" data-testid="by-type-list">
                {Object.entries(dash.by_type || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.FileText className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{k.replace(/_/g, " ")}</div>
                    <div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By department</div>
              <div className="space-y-1.5" data-testid="by-dept-list">
                {Object.entries(dash.by_department || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.Users className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink">{k.replace(/_/g, " ")}</div>
                    <div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Overdue requests</div>
              {(dash.overdue_list || []).length === 0 ? (
                <div className="text-[12px] muted">No overdue requests. 🎯</div>
              ) : (
                <div className="space-y-2" data-testid="overdue-list">
                  {dash.overdue_list.map(r => (
                    <div key={r.id} className="p-2.5 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--danger)", background: "var(--danger-soft)" }}>
                      <Icons.AlertOctagon className="w-4 h-4" style={{ color: "var(--danger)" }} />
                      <div className="flex-1">
                        <div className="text-[12px] font-medium ink">{r.principal_email || r.principal_name} — {r.request_type}</div>
                        <div className="text-[10.5px] muted">Due {r.due_by?.slice(0, 10)} · {Math.abs(r.sla_hours_remaining || 0)}h overdue</div>
                      </div>
                      <Btn size="sm" variant="secondary" onClick={() => openDetail(r.id)}>Open</Btn>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Latest activity</div>
              {(dash.activity_feed || []).length === 0 ? (
                <div className="text-[12px] muted">No activity.</div>
              ) : (
                <div className="space-y-2" data-testid="activity-feed">
                  {dash.activity_feed.map(ev => (
                    <div key={ev.id} className="flex items-start gap-2 p-2 rounded-md" style={{ border: "1px solid var(--border)" }}>
                      <Icons.Activity className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--primary)" }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11.5px] ink truncate">
                          <b>{ev.action.replace(/_/g, " ")}</b> · {ev.request_type} · {ev.principal}
                        </div>
                        <div className="text-[10px] muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(tab === "list" || tab === "kanban") && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
          <TextInput data-testid="filter-search" placeholder="Search..." value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
          <SelectInput data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">All statuses</option>
            {meta.statuses.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </SelectInput>
          <SelectInput data-testid="filter-type" value={fType} onChange={(e) => setFType(e.target.value)}>
            <option value="">All types</option>
            {meta.request_types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </SelectInput>
          <SelectInput data-testid="filter-dept" value={fDept} onChange={(e) => setFDept(e.target.value)}>
            <option value="">All departments</option>
            {meta.departments.map(d => <option key={d} value={d}>{d}</option>)}
          </SelectInput>
          <SelectInput data-testid="filter-sla" value={fSla} onChange={(e) => setFSla(e.target.value)}>
            <option value="">All SLA states</option>
            {["on_track", "at_risk", "breached", "closed"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </SelectInput>
        </div>
      )}

      {tab === "list" && (
        items.length === 0 ? (
          <EmptyState title="No requests match your filters" hint="Adjust filters or create a new request." />
        ) : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["Principal", "Type", "Status", "Department", "SLA", "Due", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="requests-tbody">
                {items.map(r => (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`req-row-${r.id}`}>
                    <td className="px-4 py-2 ink text-[12px]">
                      <div className="font-medium">{r.principal_name || "—"}</div>
                      <div className="text-[10.5px] muted">{r.principal_email}</div>
                    </td>
                    <td className="px-4 py-2 muted text-[11.5px]">{r.request_type?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2"><Pill label={r.status} mapObj={STATUS_COLORS} /></td>
                    <td className="px-4 py-2 muted text-[11.5px]">{r.assigned_department || "—"}</td>
                    <td className="px-4 py-2"><Pill label={r.sla_state} mapObj={SLA_COLORS} /></td>
                    <td className="px-4 py-2 muted text-[11px]">{r.due_by?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => openDetail(r.id)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`req-open-${r.id}`}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "kanban" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {grouped.map(lane => (
            <div key={lane.key} className="min-w-[220px] surface rounded-xl p-2" style={{ border: "1px solid var(--border)" }}>
              <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[lane.key]?.color }} />
                <div className="text-[11px] font-semibold uppercase tracking-wider ink flex-1">{lane.label}</div>
                <div className="text-[10.5px] muted">{lane.items.length}</div>
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                {lane.items.map(r => (
                  <button key={r.id} onClick={() => openDetail(r.id)} data-testid={`kanban-card-${r.id}`}
                    className="w-full text-left p-2.5 rounded-md hover:shadow-sm transition-all"
                    style={{ background: "var(--bg-1)", border: "1px solid var(--border)" }}>
                    <div className="text-[11px] font-medium ink truncate">{r.principal_email || r.principal_name}</div>
                    <div className="text-[10px] muted">{r.request_type?.replace(/_/g, " ")}</div>
                    <div className="mt-1.5 flex items-center gap-1">
                      <Pill label={r.sla_state} mapObj={SLA_COLORS} />
                      {r.priority === "high" && <RiskBadge level="high" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========== NEW REQUEST ========== */}
      <Dialog open={newModal} onOpenChange={setNewModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New Data Principal Request</DialogTitle>
            <DialogDescription>Log a request received offline or by other channels.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Principal name" required><TextInput data-testid="new-name" value={newForm.principal_name} onChange={(e) => setNewForm({ ...newForm, principal_name: e.target.value })} /></Field>
            <Field label="Email" required><TextInput data-testid="new-email" type="email" value={newForm.principal_email} onChange={(e) => setNewForm({ ...newForm, principal_email: e.target.value })} /></Field>
            <Field label="Request type" required>
              <SelectInput data-testid="new-type" value={newForm.request_type} onChange={(e) => setNewForm({ ...newForm, request_type: e.target.value })}>
                {meta.request_types.map(t => <option key={t.key} value={t.key}>{t.label} ({t.dpdp_ref})</option>)}
              </SelectInput>
            </Field>
            <Field label="Priority">
              <SelectInput data-testid="new-priority" value={newForm.priority} onChange={(e) => setNewForm({ ...newForm, priority: e.target.value })}>
                {["low", "medium", "high", "critical"].map(p => <option key={p} value={p}>{p}</option>)}
              </SelectInput>
            </Field>
            <Field label="Description" className="col-span-2"><TextArea data-testid="new-desc" value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setNewModal(false)}>Cancel</Btn>
            <Btn onClick={createRequest} disabled={!newForm.principal_email} data-testid="new-save">Create</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== SLA CONFIG ========== */}
      <Dialog open={slaModal} onOpenChange={setSlaModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>SLA Configuration (days)</DialogTitle>
            <DialogDescription>Override default SLA per request type. Missing entries use the DPDP-recommended default.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            <Field label="Default (all types)"><TextInput type="number" data-testid="sla-default" value={slaForm.default || ""} onChange={(e) => setSlaForm({ ...slaForm, default: Number(e.target.value) || undefined })} /></Field>
            {meta.request_types.map(t => (
              <Field key={t.key} label={`${t.label} (${t.default_sla_days}d default)`}>
                <TextInput type="number" data-testid={`sla-${t.key}`} value={slaForm[t.key] || ""} onChange={(e) => setSlaForm({ ...slaForm, [t.key]: Number(e.target.value) || undefined })} />
              </Field>
            ))}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setSlaModal(false)}>Cancel</Btn>
            <Btn onClick={saveSla} data-testid="sla-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DETAIL / WORKBENCH ========== */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-5xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span>Request {detail.id?.slice(0, 8)}</span>
                  <Pill label={detail.status} mapObj={STATUS_COLORS} testId="detail-status" />
                  <Pill label={detail.sla_state} mapObj={SLA_COLORS} testId="detail-sla" />
                  <span className="text-[11px] mono muted">{detail.request_type}</span>
                </DialogTitle>
                <DialogDescription>
                  {detail.principal_name || "—"} · {detail.principal_email} · Due {detail.due_by?.slice(0, 10)}
                  {detail.sla_hours_remaining !== null && ` · ${Math.round(detail.sla_hours_remaining)}h remaining`}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-[72vh] overflow-y-auto">
                {/* LEFT: description & narrative */}
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Description</div>
                    <div className="text-[12.5px] ink p-3 rounded-md whitespace-pre-wrap" style={{ background: "var(--bg-2)" }}>
                      {detail.description || "—"}
                    </div>
                  </div>

                  {/* AI panel */}
                  <div className="p-3 rounded-md" style={{ background: "var(--primary-soft)", border: "1px solid var(--primary)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11.5px] font-semibold" style={{ color: "var(--primary)" }}>
                        <Icons.Sparkles className="inline w-3.5 h-3.5 mr-1" /> AI Assistance
                      </div>
                      <Btn size="sm" variant="secondary" onClick={aiAnalyze} data-testid="detail-ai-btn">
                        <Icons.RefreshCw className="w-3 h-3" /> Analyze
                      </Btn>
                    </div>
                    {detail.ai_analysis ? (
                      <div className="grid grid-cols-2 gap-2 text-[11.5px]" data-testid="ai-analysis">
                        <div><b>Classification:</b> {detail.ai_analysis.classification}</div>
                        <div><b>Suggested dept:</b> {detail.ai_analysis.suggested_department}</div>
                        <div className="col-span-2"><b>Summary:</b> {detail.ai_analysis.summary}</div>
                        {(detail.ai_analysis.risk_flags || []).length > 0 && (
                          <div className="col-span-2">
                            <b>Risk flags:</b>{" "}
                            {detail.ai_analysis.risk_flags.map(f => (
                              <span key={f} className="inline-block px-1.5 py-0.5 rounded mr-1 mono text-[10px]" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>{f}</span>
                            ))}
                          </div>
                        )}
                        {(detail.ai_analysis.missing_information || []).length > 0 && (
                          <div className="col-span-2">
                            <b>Missing info suggested:</b>
                            <ul className="list-disc pl-4 mt-0.5">
                              {detail.ai_analysis.missing_information.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11.5px] muted">Click Analyze to run AI classification, summary, department suggestion, draft response, missing-info list, and risk-flag detection.</div>
                    )}
                  </div>

                  {/* Response draft */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider muted">Response draft</div>
                      {detail.ai_analysis?.draft_response && (
                        <button onClick={() => setDraftText(detail.ai_analysis.draft_response)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid="detail-use-ai-draft">
                          Use AI draft
                        </button>
                      )}
                    </div>
                    <TextArea data-testid="detail-draft" value={draftText} rows={8} onChange={(e) => setDraftText(e.target.value)} placeholder="Draft the official response here..." />
                    <div className="flex gap-2 mt-2">
                      <Btn size="sm" variant="secondary" onClick={() => doAction("draft-response", { content: draftText }, "Draft saved")} disabled={!draftText} data-testid="detail-save-draft">
                        <Icons.Save className="w-3 h-3" /> Save draft
                      </Btn>
                      {canApprove && detail.status === "response_drafted" && (
                        <Btn size="sm" onClick={() => doAction("approve", { comment: "Approved" }, "Response approved")} data-testid="detail-approve">
                          <Icons.CheckCircle className="w-3 h-3" /> Approve
                        </Btn>
                      )}
                    </div>
                  </div>

                  {/* Response document */}
                  <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Response document</div>
                    {detail.response_document ? (
                      <div className="p-2 rounded-md flex items-center gap-2" style={{ border: "1px solid var(--border)" }} data-testid="detail-response-doc">
                        <Icons.FileText className="w-4 h-4" style={{ color: "var(--primary)" }} />
                        <div className="flex-1 text-[12px] ink truncate">{detail.response_document.filename}</div>
                        <div className="text-[10.5px] muted">{Math.round((detail.response_document.size || 0) / 1024)} KB</div>
                      </div>
                    ) : <div className="text-[11.5px] muted">No response document uploaded.</div>}
                    <div className="mt-2">
                      <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[11.5px] font-medium"
                        style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                        <Icons.Upload className="w-3.5 h-3.5" /> Upload response document
                        <input type="file" data-testid="detail-upload-response" className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadResponseDoc(e.target.files[0])} />
                      </label>
                    </div>
                  </div>

                  {/* Attachments */}
                  <div>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Attachments ({(detail.attachments || []).length})</div>
                    <div className="space-y-1.5" data-testid="detail-attachments">
                      {(detail.attachments || []).map(a => (
                        <button key={a.id} onClick={() => downloadFile(`/principal-requests/${detail.id}/attachment/${a.id}`, a.filename)}
                          className="w-full text-left p-2 rounded-md flex items-center gap-2 hover:bg-slate-50 transition-colors" style={{ border: "1px solid var(--border)" }}>
                          <Icons.Paperclip className="w-3.5 h-3.5" />
                          <div className="flex-1 text-[11.5px] ink truncate">{a.filename}</div>
                          <div className="text-[10.5px] muted">{Math.round(a.size / 1024)} KB · {a.uploaded_by}</div>
                        </button>
                      ))}
                    </div>
                    <div className="mt-2">
                      <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-[11.5px] font-medium"
                        style={{ background: "var(--bg-2)", color: "var(--ink)" }}>
                        <Icons.Upload className="w-3.5 h-3.5" /> Add internal attachment
                        <input type="file" data-testid="detail-upload-att" className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadInternalAttachment(e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* RIGHT: workflow actions + activity */}
                <div className="space-y-4">
                  {/* Assign */}
                  <div className="p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Assign to department</div>
                    <SelectInput data-testid="detail-assign-dept" value={assignForm.department} onChange={(e) => setAssignForm({ ...assignForm, department: e.target.value })}>
                      {meta.departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </SelectInput>
                    <TextInput className="mt-2" data-testid="detail-assign-to" placeholder="Assignee email (optional)" value={assignForm.assigned_to} onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })} />
                    <TextArea className="mt-2" rows={2} data-testid="detail-assign-note" placeholder="Note (optional)" value={assignForm.note} onChange={(e) => setAssignForm({ ...assignForm, note: e.target.value })} />
                    <Btn size="sm" className="mt-2 w-full" onClick={() => doAction("assign", assignForm, "Assigned")} data-testid="detail-assign-btn">
                      <Icons.UserPlus className="w-3 h-3" /> Assign
                    </Btn>
                  </div>

                  {/* Identity verify */}
                  {detail.verification_status !== "verified" && (
                    <div className="p-3 rounded-md" style={{ border: "1px solid var(--warn)", background: "var(--warn-soft)" }}>
                      <div className="text-[11.5px] font-medium mb-2" style={{ color: "var(--warn)" }}>
                        <Icons.ShieldAlert className="inline w-3.5 h-3.5 mr-1" /> Identity not yet verified
                      </div>
                      <Btn size="sm" variant="secondary" onClick={() => doAction("verify", {}, "Identity verified")} data-testid="detail-verify" className="w-full">
                        Confirm identity verification
                      </Btn>
                    </div>
                  )}

                  {/* Request info */}
                  <div className="p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Request more info</div>
                    <TextArea rows={2} data-testid="detail-info-items" placeholder="Comma-separated items you need"
                      value={infoForm.items} onChange={(e) => setInfoForm({ ...infoForm, items: e.target.value })} />
                    <TextArea className="mt-2" rows={2} data-testid="detail-info-msg" placeholder="Message to principal"
                      value={infoForm.message} onChange={(e) => setInfoForm({ ...infoForm, message: e.target.value })} />
                    <Btn size="sm" variant="secondary" className="mt-2 w-full" data-testid="detail-info-btn"
                      onClick={() => doAction("request-info", { items: infoForm.items.split(",").map(s => s.trim()).filter(Boolean), message: infoForm.message }, "Info requested from principal")}
                      disabled={!infoForm.items}>
                      Request info
                    </Btn>
                  </div>

                  {/* Complete/reject/escalate */}
                  <div className="p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Close request</div>
                    <TextArea rows={2} data-testid="detail-complete-summary" placeholder="Response summary for the principal" value={completeSummary} onChange={(e) => setCompleteSummary(e.target.value)} />
                    <Btn size="sm" className="mt-2 w-full" data-testid="detail-complete-btn"
                      onClick={() => doAction("complete", { response_summary: completeSummary, notify: true }, "Request completed")}
                      disabled={!completeSummary}>
                      <Icons.CheckCircle className="w-3 h-3" /> Complete & notify
                    </Btn>
                  </div>

                  {canApprove && (
                    <div className="p-3 rounded-md" style={{ border: "1px solid var(--danger)", background: "var(--danger-soft)" }}>
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--danger)" }}>Reject / Escalate</div>
                      <TextArea rows={2} data-testid="detail-reject-reason" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      <Btn size="sm" variant="secondary" className="mt-2 w-full" data-testid="detail-reject-btn"
                        onClick={() => doAction("reject", { reason: rejectReason }, "Rejected")}
                        disabled={!rejectReason} style={{ color: "var(--danger)" }}>
                        Reject
                      </Btn>
                      <TextArea className="mt-2" rows={2} data-testid="detail-esc-reason" placeholder="Escalation reason" value={escalateForm.reason} onChange={(e) => setEscalateForm({ ...escalateForm, reason: e.target.value })} />
                      <TextInput className="mt-2" data-testid="detail-esc-to" placeholder="Escalate to (role/email)" value={escalateForm.escalate_to} onChange={(e) => setEscalateForm({ ...escalateForm, escalate_to: e.target.value })} />
                      <Btn size="sm" className="mt-2 w-full" data-testid="detail-esc-btn"
                        onClick={() => doAction("escalate", escalateForm, "Escalated")}
                        disabled={!escalateForm.reason}>
                        <Icons.AlertTriangle className="w-3 h-3" /> Escalate
                      </Btn>
                    </div>
                  )}

                  {/* Activity log */}
                  <div className="p-3 rounded-md" style={{ border: "1px solid var(--border)" }}>
                    <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Activity trail ({(detail.activity_log || []).length})</div>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="detail-activity">
                      {(detail.activity_log || []).slice().reverse().map(ev => (
                        <div key={ev.id} className="text-[10.5px] p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                          <div className="ink"><b>{ev.action.replace(/_/g, " ")}</b></div>
                          <div className="muted">{ev.at?.slice(0, 19).replace("T", " ")} · {ev.actor}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => setDetail(null)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
